'use client';

import { useMemo } from 'react';
import Link from 'next/link';
import { Badge, Box, Button, Container, Group, Paper, SimpleGrid, Stack, Text, Title } from '@mantine/core';
import { IconArrowRight } from '@tabler/icons-react';
import type { Locale } from '../../i18n/locales';
import type { ContentPageData, SiteSettingsData } from '../../types/content';
import { CAR_COMPARISON_FORM_URL, getCarComparisonLinkProps } from '../../lib/content/comparisonLinks';
import { Header } from '../layout/Header';
import { Footer } from '../layout/Footer';
import { PublicPageShell } from '../layout/PublicPageShell';
import { useRealtimeRefresh } from '../../hooks/useRealtimeRefresh';
import { MantineNextImage } from '../ui/MantineNextImage';

const toPlainText = (value?: string) => (value ? value.replace(/<[^>]*>?/gm, '').replace(/\s+/g, ' ').trim() : '');
const CARD_IMAGE_HEIGHT = 220;
const CARD_HEIGHT = 440;

export function CompareProductPageView({
  locale,
  siteSettings,
  data,
}: {
  locale: Locale;
  siteSettings: SiteSettingsData;
  data: ContentPageData;
}) {
  const sections = (data.sections || []).filter((section) => section.title || section.body || section.imageUrl);
  const intro = data.heroSubtitle || toPlainText(data.body);
  const heroImageUrl = data.heroImageUrl || sections[0]?.imageUrl;
  const refreshSubscriptions = useMemo(
    () => [
      { table: 'content_pages', filter: `slug=eq.${data.slug}` },
      { table: 'content_page_sections', filter: `page_slug=eq.${data.slug}` },
    ],
    [data.slug],
  );
  useRealtimeRefresh(refreshSubscriptions);

  return (
    <PublicPageShell>
      <Header locale={locale} settings={siteSettings} />

      <Box py={{ base: 42, md: 68 }}>
        <Container size="xl">
          <SimpleGrid cols={{ base: 1, md: 2 }} spacing={28}>
            <Stack gap={14} justify="center">
              {data.eyebrow ? (
                <Badge
                  variant="light"
                  size="lg"
                  styles={{ root: { background: 'var(--surface-soft)', color: 'var(--text-primary)', border: '1px solid var(--surface-border)' } }}
                >
                  {data.eyebrow}
                </Badge>
              ) : null}

              <Title
                order={1}
                style={{
                  color: 'var(--text-primary)',
                  fontWeight: 600,
                  lineHeight: 1.15,
                  letterSpacing: '-0.03em',
                  fontSize: 'clamp(2.1rem, 4.6vw, 3.6rem)',
                }}
              >
                {data.title}
              </Title>

              {intro ? (
                <Text style={{ color: 'var(--text-secondary)', maxWidth: 620, lineHeight: 1.75 }}>{intro}</Text>
              ) : null}

            </Stack>

            {heroImageUrl ? (
              <Paper
                radius={36}
                p={10}
                withBorder
                style={{ borderColor: 'var(--surface-border)', background: 'var(--surface-muted)', overflow: 'hidden' }}
              >
                <MantineNextImage
                  src={heroImageUrl}
                  alt={data.heroImageAlt || data.title}
                  radius={28}
                  h={{ base: 260, md: 430 }}
                  fit="cover"
                />
              </Paper>
            ) : null}
          </SimpleGrid>
        </Container>
      </Box>

      <Container size="xl" pb={{ base: 52, md: 72 }}>
        <SimpleGrid cols={{ base: 1, md: 3 }} spacing={20}>
          {sections.map((section, index) => (
            <Paper
              key={`${section.title || 'section'}-${index}`}
              radius={30}
              p={18}
              withBorder
              style={{
                borderColor: 'var(--surface-border)',
                background: '#fff',
                display: 'flex',
                flexDirection: 'column',
                gap: 14,
                height: CARD_HEIGHT,
              }}
            >
              {section.imageUrl ? (
                <MantineNextImage
                  src={section.imageUrl}
                  alt={section.imageAlt || section.title || data.title}
                  radius={20}
                  h={CARD_IMAGE_HEIGHT}
                  fit="cover"
                />
              ) : (
                <Paper
                  radius={20}
                  h={CARD_IMAGE_HEIGHT}
                  style={{ background: 'var(--surface-soft)', border: '1px solid var(--surface-border)' }}
                />
              )}
              <Group gap={10}>
                <Box
                  style={{
                    width: 28,
                    height: 28,
                    borderRadius: 999,
                    background: 'var(--brand-primary)',
                    color: '#fff',
                    fontSize: 12,
                    fontWeight: 700,
                    display: 'grid',
                    placeItems: 'center',
                    flexShrink: 0,
                  }}
                >
                  {index + 1}
                </Box>
                <Text fw={700} style={{ color: 'var(--text-primary)' }}>
                  {section.title}
                </Text>
              </Group>
              <Text size="sm" style={{ color: 'var(--text-secondary)', lineHeight: 1.65 }}>
                {toPlainText(section.body) || intro}
              </Text>
              {index === 0 ? (
                <Button
                  mt="auto"
                  component={Link}
                  href={CAR_COMPARISON_FORM_URL}
                  {...getCarComparisonLinkProps(CAR_COMPARISON_FORM_URL)}
                  rightSection={<IconArrowRight size={16} />}
                  styles={{ root: { background: 'var(--brand-primary)', color: '#fff' } }}
                >
                  Aloita vertailu
                </Button>
              ) : (
                <Button
                  mt="auto"
                  variant="default"
                  disabled
                  styles={{ root: { background: 'var(--surface-soft)', borderColor: 'var(--surface-border)', color: 'var(--text-muted)' } }}
                >
                  Tulossa myöhemmin
                </Button>
              )}
            </Paper>
          ))}
        </SimpleGrid>
      </Container>

      <Footer locale={locale} settings={siteSettings} />
    </PublicPageShell>
  );
}
